/** No recomputed witness: every acknowledgement matches by rule identifier alone. */
export const EMPTY_WITNESS_HASHES = Object.freeze(new Map());
/**
 * The version tag every witness hash carries.
 *
 * Persisted values need a way to say which scheme produced them. If the
 * canonical form or the digest ever changes, the tag changes with it, every
 * stored hash stops matching, and every acknowledgement expires - which is the
 * safe direction: a fresh acknowledgement is requested where one is still needed
 * (R8.11), rather than an old hash accidentally matching under a new scheme.
 */
export const WITNESS_HASH_PREFIX = 'w1:';
/** Total length of a witness hash: the tag plus 64 hexadecimal digits. */
export const WITNESS_HASH_LENGTH = WITNESS_HASH_PREFIX.length + 64;
/** Orders two strings by UTF-16 code unit, which ECMAScript fixes exactly. */
function compareCodeUnits(left, right) {
    if (left < right) {
        return -1;
    }
    return left > right ? 1 : 0;
}
/**
 * Encodes a witness value as canonical text.
 *
 * The tags: `z` null, `b0` / `b1` boolean, `#<value>;` number, `s<length>:<text>`
 * string, `a<count>:` array, `u<count>:` set, `o<count>:` object with each member
 * emitted as its key's string form followed by its value's form.
 */
function encodeWitness(value, path) {
    if (value === null) {
        return 'z';
    }
    switch (typeof value) {
        case 'boolean':
            return value ? 'b1' : 'b0';
        case 'number': {
            if (!Number.isFinite(value)) {
                throw new TypeError(`witness value at ${path} is ${String(value)}: a witness carries finite numbers only`);
            }
            // `-0 === 0`, so this normalises negative zero to zero: two witnesses that
            // compare equal must not hash differently.
            const normalized = value === 0 ? 0 : value;
            return `#${String(normalized)};`;
        }
        case 'string':
            return `s${String(value.length)}:${value}`;
        case 'object':
            return encodeWitnessObject(value, path);
        default:
            throw new TypeError(`witness value at ${path} is a ${typeof value}: a witness carries null, booleans, finite numbers, strings, arrays, sets and plain objects`);
    }
}
/** The composite half of {@link encodeWitness}: arrays, sets and plain objects. */
function encodeWitnessObject(value, path) {
    if (Array.isArray(value)) {
        const items = value;
        return `a${String(items.length)}:${items
            .map((item, index) => encodeWitness(item, `${path}[${String(index)}]`))
            .join('')}`;
    }
    if (value instanceof Set) {
        const members = [...value];
        // Sorted, not deduplicated. Sorting is what makes the order the caller
        // happened to insert in irrelevant; keeping duplicates is what keeps a
        // witness holding two structurally equal members - two milestone rows of the
        // same type and date - distinguishable from one holding a single member, so
        // deleting one of them still expires the acknowledgement.
        const encoded = members
            .map((member, index) => encodeWitness(member, `${path}{${String(index)}}`))
            .sort(compareCodeUnits);
        return `u${String(encoded.length)}:${encoded.join('')}`;
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
        const kind = value.constructor?.name ?? 'class instance';
        throw new TypeError(`witness value at ${path} is a ${kind}: a witness carries plain objects, arrays and sets only`);
    }
    // `undefined` members are dropped rather than encoded, the way JSON treats
    // them: with `exactOptionalPropertyTypes` an absent optional field and one
    // explicitly set to `undefined` mean the same thing, so they must hash alike.
    const entries = Object.entries(value)
        .filter(([, member]) => member !== undefined)
        .sort(([left], [right]) => compareCodeUnits(left, right));
    const encoded = entries
        .map(([key, member]) => `${encodeWitness(key, `${path}.${key}`)}${encodeWitness(member, `${path}.${key}`)}`)
        .join('');
    return `o${String(entries.length)}:${encoded}`;
}
/**
 * The canonical text of a witness value: one form per value, whatever order its
 * keys and set members arrived in.
 *
 * Exported because it is the value a hash is taken of, so a caller hashing
 * elsewhere - `pgcrypto`'s `digest()` over the same text, for an audit query -
 * gets the same answer as {@link stableWitnessHash} does here.
 *
 * @throws TypeError when the value holds something no witness may carry: a
 *   function, a symbol, a `bigint`, `undefined` outside an object member, a
 *   non-finite number, or a class instance such as `Date` or `Map`.
 */
export function canonicalWitnessText(witness) {
    return encodeWitness(witness, 'witness');
}
/** The 64 round constants of SHA-256 (FIPS 180-4, section 4.2.2). */
const SHA256_ROUND_CONSTANTS = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
/** The eight initial hash words of SHA-256 (FIPS 180-4, section 5.3.3). */
const SHA256_INITIAL_STATE = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
];
const SHA256_BLOCK_BYTES = 64;
/**
 * Reads a word from a typed array.
 *
 * Every index below is in range; the default exists because
 * `noUncheckedIndexedAccess` types an indexed read as possibly `undefined`, and
 * a non-null assertion would hide a genuine out-of-range read just as
 * effectively.
 */
function wordAt(words, index) {
    return words[index] ?? 0;
}
/** Rotates a 32-bit word right. */
function rotr32(value, bits) {
    return ((value >>> bits) | (value << (32 - bits))) >>> 0;
}
/** Writes a 32-bit word big-endian. */
function writeUint32BE(bytes, offset, value) {
    bytes[offset] = (value >>> 24) & 0xff;
    bytes[offset + 1] = (value >>> 16) & 0xff;
    bytes[offset + 2] = (value >>> 8) & 0xff;
    bytes[offset + 3] = value & 0xff;
}
/**
 * UTF-8 bytes of a string, without `TextEncoder`.
 *
 * The package compiles against `lib: ["ES2023"]` with no DOM and no Node types,
 * so `TextEncoder` is not in scope - and depending on a host global would make
 * the digest depend on the runtime, which is the one thing a persisted hash must
 * not do. Astral code points are encoded from the pair, because iterating a
 * string yields code points rather than code units. An unpaired surrogate is
 * encoded as its own code point rather than replaced: it is not valid text, and
 * hashing it deterministically is all this needs to do.
 */
function utf8Bytes(text) {
    const bytes = [];
    for (const character of text) {
        const codePoint = character.codePointAt(0) ?? 0;
        if (codePoint < 0x80) {
            bytes.push(codePoint);
        }
        else if (codePoint < 0x800) {
            bytes.push(0xc0 | (codePoint >>> 6), 0x80 | (codePoint & 0x3f));
        }
        else if (codePoint < 0x10000) {
            bytes.push(0xe0 | (codePoint >>> 12), 0x80 | ((codePoint >>> 6) & 0x3f), 0x80 | (codePoint & 0x3f));
        }
        else {
            bytes.push(0xf0 | (codePoint >>> 18), 0x80 | ((codePoint >>> 12) & 0x3f), 0x80 | ((codePoint >>> 6) & 0x3f), 0x80 | (codePoint & 0x3f));
        }
    }
    return new Uint8Array(bytes);
}
/** The message padded to whole blocks: `0x80`, zeros, then the bit length (FIPS 180-4, 5.1.1). */
function padSha256Message(message) {
    const blocks = Math.ceil((message.length + 9) / SHA256_BLOCK_BYTES);
    const padded = new Uint8Array(blocks * SHA256_BLOCK_BYTES);
    padded.set(message, 0);
    padded[message.length] = 0x80;
    const bitLength = message.length * 8;
    writeUint32BE(padded, padded.length - 8, Math.floor(bitLength / 0x1_0000_0000));
    writeUint32BE(padded, padded.length - 4, bitLength >>> 0);
    return padded;
}
/**
 * SHA-256 of a UTF-8 string, as 64 lowercase hexadecimal digits.
 *
 * FIPS 180-4 as written: the message schedule of section 6.2.2, then the 64-round
 * compression, then the words joined big-endian. Exported for the test that pins
 * the standard's own vectors, and deliberately absent from the area barrel - the
 * package publishes {@link stableWitnessHash}, not a hash function, so no caller
 * can grow a second use of a primitive that exists for R8.11.
 */
export function sha256Hex(text) {
    const state = new Uint32Array(SHA256_INITIAL_STATE);
    const schedule = new Uint32Array(64);
    const blocks = padSha256Message(utf8Bytes(text));
    for (let offset = 0; offset < blocks.length; offset += SHA256_BLOCK_BYTES) {
        for (let index = 0; index < 16; index += 1) {
            const at = offset + index * 4;
            schedule[index] =
                ((wordAt(blocks, at) << 24) |
                    (wordAt(blocks, at + 1) << 16) |
                    (wordAt(blocks, at + 2) << 8) |
                    wordAt(blocks, at + 3)) >>>
                    0;
        }
        for (let index = 16; index < 64; index += 1) {
            const back15 = wordAt(schedule, index - 15);
            const back2 = wordAt(schedule, index - 2);
            const sigma0 = (rotr32(back15, 7) ^ rotr32(back15, 18) ^ (back15 >>> 3)) >>> 0;
            const sigma1 = (rotr32(back2, 17) ^ rotr32(back2, 19) ^ (back2 >>> 10)) >>> 0;
            schedule[index] =
                (wordAt(schedule, index - 16) + sigma0 + wordAt(schedule, index - 7) + sigma1) >>> 0;
        }
        let a = wordAt(state, 0);
        let b = wordAt(state, 1);
        let c = wordAt(state, 2);
        let d = wordAt(state, 3);
        let e = wordAt(state, 4);
        let f = wordAt(state, 5);
        let g = wordAt(state, 6);
        let h = wordAt(state, 7);
        for (let index = 0; index < 64; index += 1) {
            const bigSigma1 = (rotr32(e, 6) ^ rotr32(e, 11) ^ rotr32(e, 25)) >>> 0;
            const choose = ((e & f) ^ (~e & g)) >>> 0;
            const temp1 = (h + bigSigma1 + choose + wordAt(SHA256_ROUND_CONSTANTS, index) + wordAt(schedule, index)) >>>
                0;
            const bigSigma0 = (rotr32(a, 2) ^ rotr32(a, 13) ^ rotr32(a, 22)) >>> 0;
            const majority = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
            const temp2 = (bigSigma0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }
        state[0] = (wordAt(state, 0) + a) >>> 0;
        state[1] = (wordAt(state, 1) + b) >>> 0;
        state[2] = (wordAt(state, 2) + c) >>> 0;
        state[3] = (wordAt(state, 3) + d) >>> 0;
        state[4] = (wordAt(state, 4) + e) >>> 0;
        state[5] = (wordAt(state, 5) + f) >>> 0;
        state[6] = (wordAt(state, 6) + g) >>> 0;
        state[7] = (wordAt(state, 7) + h) >>> 0;
    }
    return [...state].map((word) => word.toString(16).padStart(8, '0')).join('');
}
/**
 * The stable hash of a witness value (R8.11): the version tag followed by the
 * SHA-256 of the witness's canonical text.
 *
 * Deterministic across processes and releases for equal witnesses, which is the
 * whole requirement: the value is written to
 * `warning_acknowledgement.witness_hash` by one request and compared by another.
 * 67 characters, within the 128 the acknowledgement contract allows.
 */
export function stableWitnessHash(witness) {
    return `${WITNESS_HASH_PREFIX}${sha256Hex(canonicalWitnessText(witness))}`;
}
/** Whether a string has the shape {@link stableWitnessHash} produces. */
export function isWitnessHash(value) {
    return (value.length === WITNESS_HASH_LENGTH &&
        value.startsWith(WITNESS_HASH_PREFIX) &&
        /^[0-9a-f]{64}$/.test(value.slice(WITNESS_HASH_PREFIX.length)));
}
/**
 * The witness of a rule for this snapshot, or `undefined` when the rule defines
 * none.
 *
 * Exported for R8.8: the audit entry records "the acknowledged field values",
 * which are the witness itself rather than its hash. The hash is what expiry
 * compares; the value is what an auditor reads.
 */
export function ruleWitness(rule, snapshot) {
    return rule.witness?.(snapshot);
}
/**
 * The stable witness hash of a rule for this snapshot, or `undefined` when the
 * rule defines no witness - an information-tier context rule has no field values
 * to expire against.
 */
export function ruleWitnessHash(rule, snapshot) {
    if (rule.witness === undefined) {
        return undefined;
    }
    return stableWitnessHash(rule.witness(snapshot));
}
/**
 * The recomputed witness hash of every rule in `rules` that defines one.
 *
 * What the gate takes as its witness argument (R8.11). Computed over the rules
 * rather than over the findings, so a rule whose warning condition is no longer
 * met still contributes a hash: R8.11's "a fresh acknowledgement is required if
 * the warning condition still holds" is the gate's business, and a witness that
 * appeared only when a finding did would make the hash depend on the verdict.
 */
export function witnessHashesOf(rules, snapshot) {
    const hashes = new Map();
    for (const rule of rules) {
        const hash = ruleWitnessHash(rule, snapshot);
        if (hash !== undefined) {
            hashes.set(rule.id, hash);
        }
    }
    return hashes;
}
//# sourceMappingURL=witness-hash.js.map